import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppUser } from '../../global';

export type AuthStatus = 'checking' | 'signed_out' | 'signed_in';

export interface AuthHookState {
  status: AuthStatus;
  user: AppUser | null;
  calendarConnected: boolean;
  error: string | null;
  isSigningIn: boolean;
}

/**
 * Oturum durumunun tek sahibi.
 *
 * Uygulama açılışta `checking` durumunda başlar; kalıcı bir Supabase oturumu
 * varsa doğrudan içeri alınır, yoksa giriş ekranı gösterilir. Giriş Google
 * üzerinden yapılıyor ve aynı onay takvim iznini de kapsıyor — bu yüzden
 * `calendarConnected` ayrı bir adım değil, girişin doğal sonucu.
 */
export function useAuth() {
  const [state, setState] = useState<AuthHookState>({
    status: 'checking',
    user: null,
    calendarConnected: false,
    error: null,
    isSigningIn: false,
  });

  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const auth = await window.zabit.getAuthState();
      if (!mountedRef.current) return;
      // Web: Google'dan hatayla dönüldüyse (kapalı beta, askıya alınmış hesap…) giriş ekranında göster.
      const redirectError = auth.signedIn ? null : window.zabit.takeSignInError?.() ?? null;

      setState((prev) => ({
        ...prev,
        status: auth.signedIn ? 'signed_in' : 'signed_out',
        user: auth.user ?? null,
        calendarConnected: auth.calendarConnected,
        ...(redirectError ? { error: redirectError } : {}),
      }));
    } catch (err) {
      if (!mountedRef.current) return;
      setState((prev) => ({
        ...prev,
        status: 'signed_out',
        error: (err as Error).message,
      }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  const signIn = useCallback(async () => {
    setState((prev) => ({ ...prev, isSigningIn: true, error: null }));
    try {
      const result = await window.zabit.signIn();
      if (!mountedRef.current) return;

      if (result.ok) {
        setState({
          status: 'signed_in',
          user: result.state.user ?? null,
          calendarConnected: result.state.calendarConnected,
          error: null,
          isSigningIn: false,
        });
      } else {
        setState((prev) => ({ ...prev, isSigningIn: false, error: result.error }));
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setState((prev) => ({
        ...prev,
        isSigningIn: false,
        error: (err as Error).message,
      }));
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await window.zabit.signOut();
    } finally {
      if (mountedRef.current) {
        setState({
          status: 'signed_out',
          user: null,
          calendarConnected: false,
          error: null,
          isSigningIn: false,
        });
      }
    }
  }, []);

  /** Ayarlar ekranı yalnızca yöneticilere görünür. */
  const isAdmin = state.user?.role === 'admin';

  return { ...state, isAdmin, signIn, signOut, refresh };
}
